import { useHistory } from 'react-router-dom'
import { ROUTES } from '../routes'
import NormalLayoutContainer from './shared/NormalLayoutContainer'
import React, { useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ObjectParam, StringParam, useQueryParams, ArrayParam } from 'use-query-params'
import { Spinner } from './shared/Spinner'
import PageTitle from '../components/page-title'
import InvoiceSummary from '../components/invoice/summary'
import InvoicesSearchForm from '../components/invoice/invoices-search-form'
import OverdueAgingChart from '../components/invoice/Chart/OverDueAgingChart'
import QUERY_KEYS from '../graphql/queryKeys'
import invoicesAmountSummary from '../graphql/queries/invoicesAmountSummary'
import invoiceSettingsQuery from '../graphql/queries/invoiceSettings'
import clientsAll from '../graphql/queries/clientsAll'
import useQuery from '../hooks/useQuery'
import { captureErrorAndNotify } from '../utilities/errorHandlers'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../pages/hauler/clients/action/clientAction'

export default function InvoiceChart () {
  const dispatch = useDispatch()
  const history = useHistory()
  const {
    hauler,
    isWastepayApproved,
    isWastepayEnabled,
    allClients,
    dataLoaders
  } = useSelector(({ user, allClients, dataLoaders }) => ({
    hauler: user.hauler,
    isWastepayApproved: user.hauler.isWastepayApproved,
    isWastepayEnabled: user.hauler.isWastepayEnabled,
    dataLoaders,
    allClients
  }))

  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    status: ArrayParam,
    pager: ObjectParam,
    invoiceNumber: StringParam,
    accountName: StringParam,
    startDate: StringParam,
    endDate: StringParam
  })

  const setQueryParamsReplace = useCallback(
    (params) => setQueryParams(params, 'replace'),
    [setQueryParams]
  )

  useEffect(
    function setDefaultQueryParams () {
      const defaultQueryParams = {}
      if (!queryParams.sort) {
        defaultQueryParams.sort = { column: 'createdAt', direction: 'desc' }
      }
      setQueryParamsReplace({ ...queryParams, ...defaultQueryParams })
    },
    [queryParams, setQueryParamsReplace]
  )

  const { data: invoiceSettingsData } = useQuery(
    [QUERY_KEYS.invoiceSettings, hauler.id, true],
    invoiceSettingsQuery,
    {
      onError: (error) => captureErrorAndNotify(error, 'Error Fetching Invoice Settings')
    }
  )

  const invoiceProvider = invoiceSettingsData?.invoiceSettings?.sendInvoicesFrom

  const fetchAllClients = useCallback(() => {
    clientsAll({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.clientsAll,
        hauler.id,
        { column: 'name', direction: 'DESC' },
        null,
        null,
        null,
        null,
        null
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_ALL_CLIENTS_SUCCESS, payload: { clients: resp.clientsAll } })
      })
      .catch((error) => {
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })
  }, [dataLoaders, dispatch, hauler.id])

  useEffect(() => {
    fetchAllClients()
  }, [fetchAllClients])

  const {
    data: dashboardData,
    isFetching: isFetchingDashboard
  } = useQuery(
    [
      QUERY_KEYS.invoicesAmountSummary,
      hauler.id,
      queryParams.invoiceNumber,
      queryParams.accountName,
      queryParams.status,
      queryParams.startDate,
      queryParams.endDate,
      null
    ],
    invoicesAmountSummary,
    {
      enabled: Boolean(queryParams.sort),
      initialData: { invoicesamountsummaryV2: {} },
      onError: (error) => {
        captureErrorAndNotify(error, 'Failed to fetch invoice dashboard data')
      }
    }
  )

  function handleSearch ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  function clearForm ({ invoiceNumber, accountName, status, startDate, endDate }) {
    setQueryParamsReplace({ invoiceNumber, accountName, status, startDate, endDate })
  }

  function navigateToInvoicesWithStatus (status) {
    const params = new URLSearchParams()

    Object.entries({
      invoiceNumber: queryParams.invoiceNumber,
      accountName: queryParams.accountName,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    }).filter(([, v]) => v != null)
      .forEach(([k, v]) => params.append(k, v))

    if (Array.isArray(status)) {
      status.filter(Boolean).forEach(s => params.append('status', s))
    } else if (status != null) {
      params.append('status', status)
    }

    history.push({
      pathname: ROUTES.invoices,
      search: params.toString()
    })
  }

  function handleSummaryTabSelect ({ status }) {
    navigateToInvoicesWithStatus(Array.isArray(status) ? status[0] : status)
  }

  function handleAgingBarClick ({ status }) {
    navigateToInvoicesWithStatus(status)
  }

  const summaryData = dashboardData?.invoicesamountsummaryV2?.summary
  const agingBuckets = dashboardData?.invoicesamountsummaryV2?.agingBuckets

  return (
    <NormalLayoutContainer showBackLink>
      <PageTitle>Invoice Summary</PageTitle>
      <Spinner isFetching={allClients.isFetching}>
        <InvoicesSearchForm
          onSubmit={handleSearch}
          onClear={clearForm}
          isFetching={isFetchingDashboard}
          isWastepayApproved={isWastepayApproved}
          isWastepayEnabled={isWastepayEnabled}
          excludeStatuses={['VOIDED']}
          clients={allClients.clients}
          initialValues={{
            invoiceNumber: queryParams.invoiceNumber,
            accountName: queryParams.accountName,
            status: queryParams.status,
            startDate: queryParams.startDate,
            endDate: queryParams.endDate
          }}
          invoiceProvider={invoiceProvider ?? ''}
        />
      </Spinner>

      {isFetchingDashboard || !dashboardData?.invoicesamountsummaryV2?.summary?.length
        ? <Spinner isFetching />
        : (
          <InvoiceSummary
            onSubmit={handleSummaryTabSelect}
            activeStatus={
              Array.isArray(queryParams.status)
                ? queryParams.status[0]
                : queryParams.status
            }
            summaryData={summaryData}
          />
          )}

      {!isFetchingDashboard && agingBuckets?.length > 0 && (
        <OverdueAgingChart
          agingBuckets={agingBuckets}
          onBarClick={handleAgingBarClick}
        />
      )}
    </NormalLayoutContainer>
  )
}
